<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useCheckNgWords } from '@/api/queries'
import { useSelectionStore } from '@/stores/selection'
import { useNgMarkStore } from '@/stores/ngMark'
import { openExternal } from '@/utils/links'
import type { Product } from '@/types/api'

const props = defineProps<{
  product: Product | null | undefined
}>()

const selection = useSelectionStore()
const ngMark = useNgMarkStore()

// 折りたたみ状態。本文にマーキングするようになりリストを常時開く必要が
// なくなったため、既定は折りたたみ。状態はユーザーごとに永続化する。
const STORAGE_KEY = 'salmon.ngWordPanel.expanded'
const expanded = ref<boolean>(localStorage.getItem(STORAGE_KEY) === '1')
watch(expanded, (v) => {
  try {
    localStorage.setItem(STORAGE_KEY, v ? '1' : '0')
  } catch {
    // プライベートブラウジング等で書き込めない場合は無視する
  }
})
const { mutate, data, isPending } = useCheckNgWords()

// 判定結果をストアへ共有する。本文側(NgMarkedText)が同じデータを参照することで、
// 左のリストの件数と本文のマーク数が必ず一致するようにしている。
watch(data, (d) => ngMark.setNgWords(d?.ngWords), { immediate: true })

// 商品情報の全文(ヒット件数のカウント対象)。OfferDetailPanelのハイライト対象と揃える。
const productText = computed(() => {
  const p = props.product
  if (!p) return ''
  return [p.Title, p.Model, p.Color, p.Size, p.Features, p.EditorialReview]
    .filter(Boolean)
    .join('\n')
})

/**
 * ヒット件数を数える。OfferDetailPanel の highlightText と同じ
 * 「ひらがな/カタカナを区別しない・大文字小文字を区別しない」判定にする。
 */
function hitCount(word: string): number {
  if (!word || !productText.value) return 0
  const pattern = word
    .split('')
    .map((char) => {
      const code = char.charCodeAt(0)
      if (code >= 0x3041 && code <= 0x3096) {
        return `[${char}${String.fromCharCode(code + 0x0060)}]`
      }
      if (code >= 0x30a1 && code <= 0x30f6) {
        return `[${String.fromCharCode(code - 0x0060)}${char}]`
      }
      return char.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    })
    .join('')
  const matches = productText.value.match(new RegExp(pattern, 'gi'))
  return matches ? matches.length : 0
}

// 一覧の行を選択するたびに自動でNGワードを再チェックする(元VB.NET版と同じ挙動)
watch(
  () => props.product,
  (product) => {
    if (!product) return
    mutate({
      title: product.Title ?? undefined,
      model: product.Model ?? undefined,
      color: product.Color ?? undefined,
      size: product.Size ?? undefined,
      features: product.Features ?? undefined,
      description: product.EditorialReview ?? undefined,
    })
  },
  { immediate: true },
)

function refresh() {
  if (props.product) {
    mutate({
      title: props.product.Title ?? undefined,
      model: props.product.Model ?? undefined,
      color: props.product.Color ?? undefined,
      size: props.product.Size ?? undefined,
      features: props.product.Features ?? undefined,
      description: props.product.EditorialReview ?? undefined,
    })
  }
}

function levelClass(level: number) {
  if (level >= 9) return 'lv9'
  if (level >= 7) return 'lv7'
  return 'lv5'
}

function toggleNgWord(word: string) {
  if (selection.selectedNgWord === word) {
    selection.selectNgWord(null) // すでに選択中なら解除
  } else {
    selection.selectNgWord(word) // 選択する
  }
}
</script>

<template>
  <div class="ngword-section" :class="expanded ? 'expanded' : 'collapsed'">
    <div class="ngword-head ngword-head--toggle" @click="expanded = !expanded">
      <div class="section-title">
        <svg class="icon" viewBox="0 0 24 24"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
        NGワードリスト
      </div>
      <!-- 折りたたみ中でもヒット件数だけは分かるようにする -->
      <span class="ngword-count">
        {{ isPending ? '判定中…' : `${data?.ngWords.length ?? 0}件` }}
      </span>
      <button class="ng-refresh" title="再チェック" @click.stop="refresh">
        <svg class="icon icon-sm" viewBox="0 0 24 24"><polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/></svg>
      </button>
      <svg class="icon icon-sm ngword-caret" viewBox="0 0 24 24" :style="{ transform: expanded ? '' : 'rotate(-90deg)' }">
        <polyline points="6 9 12 15 18 9"/>
      </svg>
    </div>
    <div class="ngword-body">
      <div v-if="isPending" class="empty-state"><span class="spinner"></span> 判定中…</div>
      <div v-else-if="!data || data.ngWords.length === 0" class="ng-empty">
        <svg class="icon" viewBox="0 0 24 24"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
        該当するNGワードはありません
      </div>
      <div 
        v-else 
        class="ngrow" 
        :class="[levelClass(w.level), { selected: selection.selectedNgWord === w.word }]" 
        v-for="w in data.ngWords" 
        :key="w.word"
        @click="toggleNgWord(w.word)"
      >
        <div class="ngrow-word">{{ w.word }}</div>
        <div class="ngrow-reason">{{ w.reason }}</div>
        <div class="ngrow-side">
          <span
            class="ngrow-hits"
            :class="{ zero: hitCount(w.word) === 0 }"
            :title="hitCount(w.word) > 0 ? '商品情報内のヒット件数' : '商品情報内にヒットなし'"
          >
            {{ hitCount(w.word) }}
          </span>
          <a
            v-if="w.link"
            class="ngrow-link"
            title="参考リンクを開く"
            @click.stop="openExternal(w.link)"
          >
            <svg class="icon icon-xs" viewBox="0 0 24 24"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>
          </a>
          <span class="ngrow-level">Lv.{{ w.level }}</span>
        </div>
      </div>
    </div>
  </div>
</template>
